import { initializeApp } from "firebase/app";
import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import {
  connectDatabaseEmulator,
  get,
  getDatabase,
  push,
  ref,
  serverTimestamp,
  update,
} from "firebase/database";
import type {
  CartItem,
  CurrentOrders,
  CustomerDetails,
  GarmentOrder,
  SentOrder,
} from "../types";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "AIzaSyAeRD4ISgiNrpHk_vjwIFrrwZdkoi8MR84",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "kils-oks-klader.firebaseapp.com",
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL ?? "https://kils-oks-klader.firebaseio.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "kils-oks-klader",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "471974339658",
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);
const useFirebaseEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";

if (useFirebaseEmulators) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectDatabaseEmulator(database, "127.0.0.1", 9000);
}

const usernameAliases: Record<string, string> = {
  kilsok: "kilsklubbklader@gmail.com",
  admin: "kilsklubbkladeransvarig@gmail.com",
};

const defaultAdminIds = ["xAl9vG0wSUeF5UUG9IFhOYmLOPc2"];
const adminIds = (import.meta.env.VITE_FIREBASE_ADMIN_UIDS ?? defaultAdminIds.join(","))
  .split(",")
  .map((id: string) => id.trim())
  .filter(Boolean);

const localAdminEmails = (import.meta.env.VITE_FIREBASE_ADMIN_EMAILS ?? "")
  .split(",")
  .map((email: string) => email.trim().toLowerCase())
  .filter(Boolean);

export function isAdminUser(user: User) {
  const email = user.email?.toLowerCase();
  return adminIds.includes(user.uid) ||
    (useFirebaseEmulators && email !== undefined && localAdminEmails.includes(email));
}

export const observeAuth = (callback: (user: User | null) => void) =>
  onAuthStateChanged(auth, callback);

export async function signIn(username: string, password: string) {
  const normalized = username.trim().toLowerCase();
  const email = usernameAliases[normalized] ?? normalized;
  return signInWithEmailAndPassword(auth, email, password);
}

export const signOut = () => firebaseSignOut(auth);

function snapshotList<T>(value: unknown): T[] {
  if (!value || typeof value !== "object") return [];
  return Object.values(value as Record<string, T>);
}

export async function fetchOrders(): Promise<CurrentOrders> {
  const snapshot = await get(ref(database, "orders"));
  const value = snapshot.val() as Record<string, Record<string, GarmentOrder>> | null;
  if (!value) return {};

  return Object.fromEntries(
    Object.entries(value).map(([garment, orders]) => [garment, snapshotList<GarmentOrder>(orders)]),
  );
}

export async function fetchOrderCounts() {
  const orders = await fetchOrders();
  return Object.fromEntries(
    Object.entries(orders).map(([garment, garmentOrders]) => [
      garment,
      garmentOrders.reduce(
        (total, order) => total + Object.values(order.sizes).reduce((sum, count) => sum + count, 0),
        0,
      ),
    ]),
  );
}

export async function fetchSentOrders(): Promise<SentOrder[]> {
  const snapshot = await get(ref(database, "sentOrders"));
  const orders = snapshotList<SentOrder>(snapshot.val());
  return orders
    .map((order) => ({
      ...order,
      garments: Object.fromEntries(
        Object.entries(order.garments ?? {}).map(([garment, garmentOrders]) => [
          garment,
          snapshotList<GarmentOrder>(garmentOrders),
        ]),
      ),
    }))
    .sort((a, b) => b.date - a.date);
}

export async function submitOrder(
  customer: CustomerDetails,
  items: CartItem[],
) {
  const grouped = items.reduce<Record<string, Record<string, number>>>((result, item) => {
    result[item.garmentName] ??= {};
    result[item.garmentName][item.size] = (result[item.garmentName][item.size] ?? 0) + 1;
    return result;
  }, {});

  const writes: Record<string, unknown> = {};
  for (const [garment, sizes] of Object.entries(grouped)) {
    const orderKey = push(ref(database, `orders/${garment}`)).key;
    if (!orderKey) throw new Error("Kunde inte skapa beställningen.");
    writes[`orders/${garment}/${orderKey}`] = {
      date: serverTimestamp(),
      mail: customer.email,
      name: customer.name,
      phone: customer.phone,
      sizes,
    };
  }
  await update(ref(database), writes);
}

export async function markOrdersAsSent(garmentNames: string[]) {
  const garments: Record<string, unknown> = {};
  for (const garmentName of garmentNames) {
    const snapshot = await get(ref(database, `orders/${garmentName}`));
    if (snapshot.exists()) garments[garmentName] = snapshot.val();
  }

  const sentKey = push(ref(database, "sentOrders")).key;
  if (!sentKey) throw new Error("Kunde inte skapa leveransen.");

  const writes: Record<string, unknown> = {
    [`sentOrders/${sentKey}`]: { date: serverTimestamp(), garments },
  };
  garmentNames.forEach((garmentName) => {
    writes[`orders/${garmentName}`] = null;
  });
  await update(ref(database), writes);
}
